import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWarmTheme } from '../theme/ThemeContext';
import { useLauncher } from '../components/home/useLauncher';
import { TOOLS, TOOL_BY_KEY } from '../components/home/toolRegistry';
import { buildOrderedGroups } from '../components/home/sidebarShared';
import NotificationsBell from '../components/NotificationsBell';
import { Sprite } from '../components/home/dw/Icons';
import { DwSidebar, DwTopBar } from '../components/home/dw/Shell';
import axios from 'axios';
import { Hero, JarvisCore, Ticker } from '../components/home/dw/TopBlocks';
import { PacePanel } from '../components/home/dw/PacePanel';
import { ActionQueue, RightColumn, FILTERS } from '../components/home/dw/Queue';
import { Scrim, Palette, DraftDrawer, KeysModal, TaskModal, FavModal, Toasts } from '../components/home/dw/Overlays';
import { useHomeData } from '../components/home/dw/useHomeData';
import { ActionSheet } from '../components/jarvis/ActionSheet';
import { jv } from '../components/jarvis/api';
import '../styles/derwent-home.css';

// DerwentAI Home — 1:1 implementation of the approved prototype (derwentai-home.html), on live workspace data.
const HomePage = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const { mode, toggleMode } = useWarmTheme();
  const { allowed, favourites, launch, groupOrder, toolOrder, saveFavourites } = useLauncher();
  const groups = useMemo(() => buildOrderedGroups(allowed, groupOrder, toolOrder), [allowed, groupOrder, toolOrder]);
  const allowedTools = useMemo(() => TOOLS.filter((t) => allowed.has(t.key)), [allowed]);
  const favTools = useMemo(() => (favourites || []).map((k) => TOOL_BY_KEY[k]).filter(Boolean), [favourites]);
  const firstName = user?.name?.split(' ')[0];

  const [period] = useState(() => localStorage.getItem('dw_home_period') || 'week');
  const data = useHomeData({ token, userId: user?.id, period, me: firstName });
  const { queue, summary } = data;

  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState(0);
  const [jarvis, setJarvis] = useState(null);
  useEffect(() => { if (!token) return undefined; const f = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis-link/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data: d }) => setJarvis(d)).catch(() => {}); f(); const t = setInterval(f, 60000); return () => clearInterval(t); }, [token]);
  const refreshRef = React.useRef(data.refresh); refreshRef.current = data.refresh;
  const [cos, setCos] = useState(null);
  const [jvItem, setJvItem] = useState(null);
  const loadCos = useCallback(() => { if (token) jv.summary(token).then(setCos).catch(() => {}); }, [token]);
  useEffect(() => { loadCos(); const t = setInterval(loadCos, 120000); const on = () => { loadCos(); refreshRef.current(); }; window.addEventListener('jarvis:changed', on); return () => { clearInterval(t); window.removeEventListener('jarvis:changed', on); }; }, [loadCos]);
  const [overlay, setOverlay] = useState(null); // 'palette' | 'draft' | 'keys' | 'task' | 'fav' | 'side'
  const [draftItem, setDraftItem] = useState(null);
  const [newId, setNewId] = useState(null);
  const [clock, setClock] = useState('');
  useEffect(() => { const f = () => setClock(new Date().toLocaleTimeString('en-AU', { timeZone: 'Australia/Hobart', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })); f(); const t = setInterval(f, 1000); return () => clearInterval(t); }, []);

  const visible = queue.items.filter(FILTERS.find((f) => f.id === filter).test);
  useEffect(() => { if (sel >= visible.length) setSel(Math.max(0, visible.length - 1)); }, [visible.length, sel]);

  const closeAll = useCallback(() => { setOverlay(null); if (document.activeElement?.closest('.palette,.drawer,.modal')) document.activeElement.blur(); }, []);
  const go = useCallback((path) => { closeAll(); navigate(path); }, [navigate, closeAll]);
  const openAndi = useCallback((message) => { closeAll(); window.dispatchEvent(new CustomEvent('hey-andi:open', { detail: message ? { message } : undefined })); }, [closeAll]);
  const openDraft = useCallback((q) => { setDraftItem(q); setOverlay('draft'); }, []);
  const scrollQueue = useCallback(() => { setSel(0); document.getElementById('queue')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);

  const runCommand = useCallback((kind, arg) => {
    if (kind === 'draft') openDraft(arg);
    else if (kind === 'task') setOverlay('task');
    else if (kind === 'andi') openAndi();
    else if (kind === 'theme') toggleMode();
    else if (kind === 'keys') setOverlay('keys');
    else if (kind === 'go') go(arg);
    else if (kind === 'launch') { closeAll(); launch(arg); }
  }, [openDraft, openAndi, toggleMode, go, launch, closeAll]);

  const submitTask = async (client, what) => {
    try { const item = await data.addTask(client, what); setFilter('all'); setSel(0); setNewId(item.id); closeAll(); setTimeout(() => setNewId(null), 1800); }
    catch { data.toast("Couldn't add that task.", null, 'x'); }
  };
  const sendDraft = (draft) => { const q = draftItem; closeAll(); if (q) setTimeout(() => data.send(q, draft), 220); };

  // Keyboard: ⌘K palette, Esc, J/K move, E done, D/Enter draft, S snooze, N task, A Andi, T theme, ? shortcuts.
  useEffect(() => {
    const onKey = (e) => {
      const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '') || document.activeElement?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOverlay((o) => (o === 'palette' ? null : 'palette')); return; }
      if (e.key === 'Escape') { closeAll(); return; }
      if (typing || overlay || e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase(); const cur = visible[sel];
      if (k === 'j') setSel((s) => Math.min(visible.length - 1, s + 1));
      else if (k === 'k') setSel((s) => Math.max(0, s - 1));
      else if (k === 'e' && cur) document.querySelector(`[data-id="${cur.id}"] [data-testid^="queue-done"], [data-id="${cur.id}"] [data-testid^="queue-open"]`)?.click();
      else if (k === 's' && cur) document.querySelector(`[data-id="${cur.id}"] [data-testid^="queue-snooze"]`)?.click();
      else if ((k === 'd' || k === 'enter') && cur) { e.preventDefault(); openDraft(cur); }
      else if (k === 'n') { e.preventDefault(); setOverlay('task'); }
      else if (k === 't') toggleMode();
      else if (e.key === '?') setOverlay('keys');
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [overlay, visible, sel, closeAll, openDraft, openAndi, toggleMode]);

  useEffect(() => { const el = document.querySelector('.dwh .item.sel'); el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [sel]);

  return (
    <div className="dwh" data-theme={mode === 'dark' ? 'dark' : 'light'} data-testid="home-main">
      <Sprite />
      <div className="app">
        <DwSidebar user={user} favTools={favTools} groups={groups} act="home" onNav={go} onLaunch={(t) => { closeAll(); launch(t); }} onLogout={logout} onEditFav={() => setOverlay('fav')} open={overlay === 'side'} onClose={closeAll} token={token} />
        <div className="main">
          <DwTopBar clock={clock} theme={mode} onPalette={() => setOverlay('palette')} onTheme={toggleMode} onKeys={() => setOverlay('keys')} onMenu={() => setOverlay('side')}
            bell={<NotificationsBell />} />
          <main className="wrap">
            <section className="hero">
              <Hero firstName={firstName} queue={queue} onStart={scrollQueue} onGamePlan={() => go('/game-plan')} />
              <JarvisCore j={jarvis} cos={cos} onOpen={() => go('/jarvis')} onManage={() => go('/integrations/jarvis')} onChat={() => window.dispatchEvent(new Event('jarvis:chat-open'))} />
            </section>
            <Ticker activity={data.activity} week={summary.week} />
            <PacePanel token={token} onGo={go} onAnalytics={() => go('/game-plan')} onGoals={() => go('/game-plan')} />
            <div className="cols">
              <ActionQueue queue={queue} filter={filter} setFilter={setFilter} sel={sel} setSel={setSel} onDone={(q) => data.complete(q)} onSnooze={(q) => data.snooze(q)} onDraft={(q) => { data.ack(q); openDraft(q); }} onAck={(q) => data.ack(q)}
                onAddTask={() => setOverlay('task')} newId={newId || data.freshId} onMore={() => go('/lead-nurture')} onJarvis={setJvItem} />
              <RightColumn favTools={favTools} onLaunch={(t) => launch(t)} onAsk={(q) => openAndi(q)} onCustomise={() => setOverlay('fav')} />
            </div>
          </main>
        </div>
      </div>

      <Scrim on={!!overlay} onClick={closeAll} />
      <Palette on={overlay === 'palette'} onClose={closeAll} queue={queue.items} tools={allowedTools} token={token} run={runCommand} onAskAndi={() => {}} />
      <DraftDrawer on={overlay === 'draft'} item={draftItem} onClose={closeAll} onSend={sendDraft} />
      <KeysModal on={overlay === 'keys'} onClose={closeAll} />
      <TaskModal on={overlay === 'task'} onClose={closeAll} onSubmit={submitTask} />
      <FavModal on={overlay === 'fav'} onClose={closeAll} tools={allowedTools} favourites={favourites || []} onSave={saveFavourites} />
      <Toasts toasts={data.toasts} onKill={data.killToast} />
      <ActionSheet token={token} insight={jvItem} open={!!jvItem} onClose={() => setJvItem(null)} onChanged={() => { data.refresh(); loadCos(); }} />
    </div>
  );
};

export default HomePage;
